<template>
  <div class="setup-step">
    <h3>{{ sprintf(s__('ProjectSetup|Step %{step}: Deadline Reports'), { step: stepNumber }) }}</h3>
    <p class="step-description">
      {{
        s__(
          "ProjectSetup|Set up regular deadline reports for this project. You'll receive a summary of tasks due soon at the specified time."
        )
      }}
    </p>

    <div class="form-row">
      <label>{{ s__('ProjectSetup|Report time') }}</label>
      <div class="time-selectors">
        <select v-model="reportHour" :disabled="saving" class="time-select">
          <option :value="null">--</option>
          <option v-for="h in 24" :key="h - 1" :value="String(h - 1).padStart(2, '0')">
            {{ String(h - 1).padStart(2, '0') }}
          </option>
        </select>
        <span>:</span>
        <select v-model="reportMinute" :disabled="saving" class="time-select">
          <option :value="null">--</option>
          <option v-for="m in [0, 15, 30, 45]" :key="m" :value="String(m).padStart(2, '0')">
            {{ String(m).padStart(2, '0') }}
          </option>
        </select>
      </div>
    </div>

    <div class="form-row">
      <label>{{ s__('ProjectSetup|Days of week') }}</label>
      <div class="days-grid">
        <label v-for="day in weekDays" :key="day.value" class="checkbox-label">
          <input v-model="reportDays" type="checkbox" :value="day.value" :disabled="saving" />
          <span>{{ day.label }}</span>
        </label>
      </div>
      <div class="help-text">
        {{ s__('ProjectSetup|Select which days you want to receive deadline reports') }}
      </div>
    </div>

    <div v-if="saveError" class="error">{{ saveError }}</div>
    <div v-if="saveSuccess" class="success">
      {{ s__('ProjectSetup|Preferences saved successfully!') }}
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useMutation } from '@vue/apollo-composable'
import { gql } from '@apollo/client/core'
import { s__, sprintf } from '@/locale'

const props = defineProps({
  project: {
    type: Object,
    required: true,
  },
  stepNumber: {
    type: Number,
    required: true,
  },
  saving: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['save', 'skip'])

const reportHour = ref(null)
const reportMinute = ref(null)
const reportDays = ref([])
const originalReportHour = ref(null)
const originalReportMinute = ref(null)
const originalReportDays = ref([])
const saveError = ref('')
const saveSuccess = ref(false)

// Week days - order based on project's language preference
const weekDays = computed(() => {
  const isRussian = props.project?.preferredLanguage === 'ru'

  const days = [
    { label: s__('ProjectSetup|Sun'), value: 0 },
    { label: s__('ProjectSetup|Mon'), value: 1 },
    { label: s__('ProjectSetup|Tue'), value: 2 },
    { label: s__('ProjectSetup|Wed'), value: 3 },
    { label: s__('ProjectSetup|Thu'), value: 4 },
    { label: s__('ProjectSetup|Fri'), value: 5 },
    { label: s__('ProjectSetup|Sat'), value: 6 },
  ]

  if (isRussian) {
    return [
      days[1], // Mon
      days[2], // Tue
      days[3], // Wed
      days[4], // Thu
      days[5], // Fri
      days[6], // Sat
      days[0], // Sun
    ]
  } else {
    return days
  }
})

const UPDATE_PROJECT_MUTATION = gql`
  mutation UpdateProject($id: ID!, $deadlineReportTime: String, $deadlineReportDays: [Int!]) {
    updateProject(
      id: $id
      deadlineReportTime: $deadlineReportTime
      deadlineReportDays: $deadlineReportDays
    ) {
      project {
        id
        deadlineReportTime
        deadlineReportDays
      }
      errors
    }
  }
`

const { mutate: updateProject } = useMutation(UPDATE_PROJECT_MUTATION)

// Initialize from project
watch(
  () => props.project,
  projectData => {
    if (projectData) {
      if (projectData.deadlineReportTime) {
        const parts = projectData.deadlineReportTime.split(':')
        reportHour.value = parts[0] || null
        reportMinute.value = parts[1] || null
      } else {
        reportHour.value = null
        reportMinute.value = null
      }
      reportDays.value = (projectData.deadlineReportDays || []).map(d => Number(d))
      originalReportHour.value = reportHour.value
      originalReportMinute.value = reportMinute.value
      originalReportDays.value = [...reportDays.value]
    }
  },
  { immediate: true }
)

// Auto-set minutes when hour is selected
watch(
  () => reportHour.value,
  newHour => {
    if (newHour && reportMinute.value === null) {
      reportMinute.value = '00'
    }
  }
)

// Check if there are unsaved changes
function hasChanges() {
  const currentDays = [...reportDays.value].sort((a, b) => a - b)
  const originalDays = [...originalReportDays.value].sort((a, b) => a - b)
  return (
    reportHour.value !== originalReportHour.value ||
    reportMinute.value !== originalReportMinute.value ||
    JSON.stringify(currentDays) !== JSON.stringify(originalDays)
  )
}

// Reset changes
function reset() {
  reportHour.value = originalReportHour.value
  reportMinute.value = originalReportMinute.value
  reportDays.value = [...originalReportDays.value]
  saveError.value = ''
  saveSuccess.value = false
}

// Expose methods to parent
defineExpose({
  hasChanges,
  reset,
  async save() {
    if (!props.project?.id) return

    saveError.value = ''
    saveSuccess.value = false

    try {
      const deadlineReportTime =
        reportHour.value && reportMinute.value !== null
          ? `${reportHour.value}:${reportMinute.value}`
          : null

      const deadlineReportDays =
        reportDays.value.length > 0 ? reportDays.value.sort((a, b) => a - b) : []

      const { data } = await updateProject({
        id: props.project.id,
        deadlineReportTime: deadlineReportTime,
        deadlineReportDays: deadlineReportDays,
      })

      if (data?.updateProject?.errors?.length > 0) {
        throw new Error(data.updateProject.errors.join(', '))
      }

      saveSuccess.value = true
      originalReportHour.value = reportHour.value
      originalReportMinute.value = reportMinute.value
      originalReportDays.value = [...reportDays.value]
      emit('save', {
        deadlineReportTime: deadlineReportTime,
        deadlineReportDays: deadlineReportDays,
      })
      return true
    } catch (e) {
      saveError.value = e.message || 'Failed to save preferences'
      return false
    }
  },
})
</script>

<style scoped>
.setup-step {
  max-width: 600px;
}

.setup-step h3 {
  margin-top: 0;
  margin-bottom: 8px;
  font-size: 20px;
  font-weight: 600;
  color: #333;
}

.step-description {
  margin-bottom: 24px;
  color: #666;
  font-size: 14px;
  line-height: 1.5;
}

.form-row {
  margin-bottom: 16px;
}

.form-row label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #555;
}

.time-selectors {
  display: flex;
  gap: 8px;
  align-items: center;
}

.time-select {
  flex: 1;
  min-width: 60px;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
}

.time-select:disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
}

.days-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  gap: 8px;
  margin-bottom: 8px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-weight: normal;
  color: #333;
}

.checkbox-label input[type='checkbox'] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.checkbox-label input[type='checkbox']:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.help-text {
  margin-top: 8px;
  font-size: 13px;
  color: #666;
}

.error {
  color: #d32f2f;
  font-size: 14px;
  margin-top: 8px;
}

.success {
  color: #2e7d32;
  font-size: 14px;
  margin-top: 8px;
}
</style>
